import { StatusCode } from "grpc-web";
import { MessageDescriptor } from "react-intl";
import { messages } from "../experiment.messages";

/**
 * Maps every backend CreateExperiment rejection EXCEPT the V7 comparability rejection (owned by
 * `parseComparabilityError`, checked separately and first by the caller) to a user-facing message.
 *
 * Of the backend's full V1-V15 validation matrix, only V2 (ticker uniqueness, ALREADY_EXISTS) and
 * V6 (a selected scenario no longer exists, NOT_FOUND) are realistically reachable through this
 * form and get a dedicated message. Every other rule is already prevented client-side by a sibling
 * story (US-011/US-012/US-014/US-015) and falls back to one shared, deliberately generic message -
 * see this story's proposal.md API Contracts table for the full rule-by-rule breakdown.
 *
 * Never string-interpolates any part of the backend's raw error message into the returned
 * `MessageDescriptor` - only one of three fixed, known-safe messages is ever returned.
 */
export function mapGenericCreateExperimentError(error: unknown): MessageDescriptor {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code: unknown }).code;
    if (code === StatusCode.ALREADY_EXISTS) {
      return messages.errors.tickerAlreadyExists; // V2
    }
    if (code === StatusCode.NOT_FOUND) {
      return messages.errors.scenarioNotFound; // V6
    }
    // V1, V3, V4, V5, V8, V9, V10, V11, V12, V13, V14, V15, and any other INVALID_ARGUMENT this
    // form structurally cannot produce today: one shared, deliberately generic message.
  }
  return messages.errors.genericSubmit;
}
